<template>
  <div class="forbrug-table-wrap">
    <table class="table is-striped is-narrow is-fullwidth forbrug-table">
      <thead>
        <tr>
          <th class="col-sticky col-booking">Booking</th>
          <th>Husstand</th>
          <th>Booket af</th>
          <th colspan="2" class="col-group">Periode</th>
          <th colspan="2" class="col-group">Km</th>
          <th class="has-text-right">Kørt</th>
          <th class="has-text-right">Kr.</th>
          <th>Noter</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(row, index) in usages" :key="row.id">
          <th class="col-sticky col-booking" scope="row">#{{ row.id }}</th>
          <td>{{ row.usersAccountName }}</td>
          <td>{{ row.usersFullName }}</td>
          <td>{{ formatPeriodStart(row.start) }}</td>
          <td>{{ formatPeriodEnd(row.start, row.end) }}</td>
          <ForbrugKmCell
            :value="row.kmStandStart"
            field="kmStandStart"
            :is-admin="isAdmin"
            :is-editing="isEditingKm(row.id, 'kmStandStart')"
            :edit-value="kmEditValue"
            :dirty="kmEditDirty"
            :saving="kmEditSaving"
            :has-gap="hasKmGap(index)"
            @start-edit="$emit('start-km-edit', row, $event)"
            @update:edit-value="$emit('update:kmEditValue', $event)"
            @save="$emit('save-km-edit')"
            @cancel="$emit('cancel-km-edit')"
          />
          <ForbrugKmCell
            :value="row.kmStandEnd"
            field="kmStandEnd"
            :is-admin="isAdmin"
            :is-editing="isEditingKm(row.id, 'kmStandEnd')"
            :edit-value="kmEditValue"
            :dirty="kmEditDirty"
            :saving="kmEditSaving"
            @start-edit="$emit('start-km-edit', row, $event)"
            @update:edit-value="$emit('update:kmEditValue', $event)"
            @save="$emit('save-km-edit')"
            @cancel="$emit('cancel-km-edit')"
          />
          <td class="has-text-right">{{ isAdminBooking(row) ? 0 : row.distance }}</td>
          <td class="has-text-right">{{ isAdminBooking(row) ? 0 : formatNumber(row.totalPrice) }}</td>
          <td class="col-notes">
            <template v-if="formatNotesParts(row).length">
              <div
                v-for="(part, partIndex) in formatNotesParts(row)"
                :key="partIndex"
                class="note-line"
              >{{ part }}</div>
            </template>
          </td>
        </tr>
        <tr class="subtotals">
          <th class="col-sticky col-booking" scope="row" colspan="2">Kørsel</th>
          <td colspan="5"></td>
          <td class="has-text-right">{{ totalDistance }}</td>
          <td class="has-text-right">{{ formatNumber(totalPrice) }}</td>
          <td></td>
        </tr>
        <tr v-if="subscription" class="subtotals">
          <th class="col-sticky col-booking" scope="row" colspan="2">
            Abonnement ({{ chauffeursLabel }})
          </th>
          <td colspan="5"></td>
          <td></td>
          <td class="has-text-right">{{ formatNumber(subscription.fee) }}</td>
          <td></td>
        </tr>
        <tr v-if="subscription" class="totals">
          <th class="col-sticky col-booking" scope="row" colspan="2">Total, måneden</th>
          <td colspan="5"></td>
          <td></td>
          <td class="has-text-right">{{ formatNumber(totalPrice + subscription.fee) }}</td>
          <td></td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import { defineComponent } from 'vue'
import ForbrugKmCell from '@/components/forbrug/ForbrugKmCell.vue'
import {
  chauffeursLabel as buildChauffeursLabel,
  formatNotesParts,
  formatNumber,
  formatPeriodEnd,
  formatPeriodStart,
  hasKmGap as calcKmGap,
  isAdminBooking,
  sumUsageDistance,
  sumUsagePrice
} from '@/forbrug/formatters'

export default defineComponent({
  name: 'ForbrugTable',
  components: { ForbrugKmCell },
  props: {
    usages: { type: Array, default: () => [] },
    subscription: { type: Object, default: null },
    isAdmin: { type: Boolean, default: false },
    vehicleFilter: { type: String, default: '' },
    kmEditBookingId: { type: [Number, String], default: null },
    kmEditField: { type: String, default: '' },
    kmEditValue: { type: String, default: '' },
    kmEditDirty: { type: Boolean, default: false },
    kmEditSaving: { type: Boolean, default: false }
  },
  emits: [
    'start-km-edit',
    'update:kmEditValue',
    'save-km-edit',
    'cancel-km-edit'
  ],
  computed: {
    totalDistance() {
      return sumUsageDistance(this.usages)
    },
    totalPrice() {
      return sumUsagePrice(this.usages)
    },
    chauffeursLabel() {
      return buildChauffeursLabel(this.subscription)
    }
  },
  methods: {
    formatNumber,
    formatPeriodStart,
    formatPeriodEnd,
    formatNotesParts,
    isAdminBooking,
    isEditingKm(bookingId, field) {
      return this.kmEditBookingId === bookingId && this.kmEditField === field
    },
    hasKmGap(index) {
      return calcKmGap(this.usages, index, !!this.vehicleFilter)
    }
  }
})
</script>

<style scoped>
.forbrug-table-wrap {
  overflow: auto;
  width: 100%;
  max-height: calc(100vh - 16rem);
  border: 1px solid #dbdbdb;
  background: #fff;
}

.forbrug-table {
  width: 100%;
  margin-bottom: 0;
  border-collapse: separate;
  border-spacing: 0;
  min-width: 52rem;
}

.forbrug-table thead th {
  position: sticky;
  top: 0;
  z-index: 3;
  background: #f5f5f5;
  box-shadow: 0 1px 0 #dbdbdb;
  white-space: nowrap;
}

.forbrug-table thead th.col-group {
  text-align: center;
}

.forbrug-table th,
.forbrug-table td {
  white-space: nowrap;
  border-left: 1px solid #dbdbdb;
}

.forbrug-table .col-notes {
  white-space: normal;
  min-width: 12rem;
  max-width: 22rem;
}

.forbrug-table .note-line + .note-line {
  margin-top: 0.25rem;
}

.forbrug-table :deep(.km-cell.is-editable) {
  cursor: pointer;
}

.forbrug-table :deep(.km-cell.is-editable:hover) {
  outline: 1px solid #b5b5b5;
  outline-offset: -1px;
}

.forbrug-table :deep(.km-edit) {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.35rem;
}

.forbrug-table :deep(.km-edit-actions) {
  display: flex;
  gap: 0.35rem;
}

.forbrug-table :deep(.km-edit-input) {
  width: 6.5rem;
  text-align: right;
  padding-left: 0.4rem;
  padding-right: 0.4rem;
}

.forbrug-table th:first-child,
.forbrug-table td:first-child,
.forbrug-table .col-booking {
  border-left: none;
}

.forbrug-table .col-sticky {
  position: sticky;
  background: #fff;
  z-index: 1;
}

.forbrug-table thead .col-sticky {
  z-index: 4;
  background: #f5f5f5;
}

.forbrug-table .col-booking {
  left: 0;
  min-width: 5.5rem;
  box-shadow: 1px 0 0 #dbdbdb;
}

.forbrug-table tbody tr:nth-child(even) .col-sticky {
  background: #fafafa;
}

.forbrug-table tbody tr.subtotals .col-sticky,
.forbrug-table tbody tr.totals .col-sticky {
  background: #f0f0f0;
}

.forbrug-table tbody tr.subtotals,
.forbrug-table tbody tr.totals {
  font-weight: 600;
}

.forbrug-table tbody tr.totals {
  background: #ebebeb;
}
</style>
